/**
 * Agent Schedules API Service
 */

import { getAuthHeaders } from './authConfig';

const API_BASE = '/api/agent-schedules';

export interface AgentSchedule {
  id: string;
  agent_id: string;
  agent_name?: string;
  cron_expression?: string;
  interval_minutes?: number;
  enabled: boolean;
  last_run_at?: string;
  next_run_at?: string;
  last_run_status?: 'passed' | 'failed' | 'running' | null;
}

export interface CreateAgentScheduleRequest {
  agent_id: string;
  cron_expression?: string;
  interval_minutes?: number;
  input_variables?: Record<string, string>;
}

/**
 * Make an authenticated fetch request with organization context.
 */
async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const authHeaders = getAuthHeaders();
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...authHeaders,
      ...options.headers,
    },
  });
  return response;
}

/**
 * List all agent schedules
 */
export async function listAgentSchedules(): Promise<AgentSchedule[]> {
  const response = await authFetch(API_BASE);
  if (!response.ok) {
    throw new Error(`Failed to list agent schedules: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Create an agent schedule
 */
export async function createAgentSchedule(data: CreateAgentScheduleRequest): Promise<AgentSchedule> {
  const response = await authFetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Failed to create agent schedule: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get an agent schedule by ID
 */
export async function getAgentSchedule(scheduleId: string): Promise<AgentSchedule> {
  const response = await authFetch(`${API_BASE}/${scheduleId}`);
  if (!response.ok) {
    throw new Error(`Failed to get agent schedule: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Trigger a schedule manually
 */
export async function triggerAgentSchedule(scheduleId: string): Promise<{ status: string; id: string }> {
  const response = await authFetch(`${API_BASE}/${scheduleId}/trigger`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to trigger agent schedule: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Update schedule enabled status
 */
export async function updateAgentSchedule(scheduleId: string, enabled: boolean): Promise<{ status: string; id: string; enabled: boolean }> {
  const response = await authFetch(`${API_BASE}/${scheduleId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(enabled),
  });
  if (!response.ok) {
    throw new Error(`Failed to update agent schedule: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Delete a schedule
 */
export async function deleteAgentSchedule(scheduleId: string): Promise<{ status: string; id: string }> {
  const response = await authFetch(`${API_BASE}/${scheduleId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete agent schedule: ${response.statusText}`);
  }
  return response.json();
}

export const agentSchedulesApi = {
  listAgentSchedules,
  createAgentSchedule,
  getAgentSchedule,
  triggerAgentSchedule,
  updateAgentSchedule,
  deleteAgentSchedule,
};

export default agentSchedulesApi;
